/* eslint-disable max-len */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, IconButton, Modal, styled, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { ContractIcon, SurveyIcon } from "./LandSummaryCard";
import { SpecialZoomLevel, Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";

const PDF_WORKER_URL = "https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js";

const BODY_FONT =
  "'Inter', -apple-system, BlinkMacSystemFont, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'";

export type MediaFile = {
  id: string | number;
  url: string;
  name?: string;
  type?: string;
};

type Kind = "image" | "youtube" | "vimeo" | "video" | "pdf" | "other";

const EFT_FILE_ID = "__eft-image__";
const EFT_TYPE_MARKER = "image/x-eft";
const isEftFile = (file: MediaFile) => file.type === EFT_TYPE_MARKER;

const isPdf = (file: MediaFile) =>
  /\.pdf(\?.*)?$/i.test(file.url) || (file.name?.toLowerCase().endsWith(".pdf") ?? false);

const isYouTube = (url: string) => /(?:youtube\.com|youtu\.be)/i.test(url);
const isVimeo = (url: string) => /vimeo\.com/i.test(url);
const isVideoFile = (url: string) => /\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)(\?.*)?$/i.test(url);

const extractYouTubeId = (url: string): string | null => {
  const match = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
  return match ? match[1] : null;
};

const extractVimeoId = (url: string): string | null => {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match ? match[1] : null;
};

const classify = (file: MediaFile): Kind => {
  if (isEftFile(file)) return "image";
  if (isPdf(file)) return "pdf";
  if (isYouTube(file.url)) return "youtube";
  if (isVimeo(file.url)) return "vimeo";
  if (isVideoFile(file.url)) return "video";
  if (file.type === "additional" || file.type?.startsWith("image/") || /\.(png|jpe?g|gif|webp|avif|svg)(\?.*)?$/i.test(file.url)) return "image";
  return "other";
};

const youTubeThumb = (url: string) => {
  const id = extractYouTubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
};

const Backdrop = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  backgroundColor: "#0b0b0b",
  display: "flex",
  flexDirection: "column",
  outline: "none",
});

const HeaderBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 24px",
  color: "#f5f5f5",
  fontFamily: BODY_FONT,
  fontSize: "14px",
  fontWeight: 600,
  flexShrink: 0,
  zIndex: 2,
  [theme.breakpoints.down("sm")]: {
    padding: "12px 16px",
  },
}));

const HeaderButton = styled(IconButton)({
  color: "#f5f5f5",
  padding: "8px",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.08)",
  },
});

const HeaderCenter = styled(Box)({
  fontFamily: BODY_FONT,
  fontSize: "14px",
  fontWeight: 600,
  color: "#f5f5f5",
});

const Stage = styled(Box)(({ theme }) => ({
  position: "relative",
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 0,
  padding: "24px 24px 0",
  [theme.breakpoints.down("sm")]: {
    padding: "18px 24px 0",
  },
}));

const StageMedia = styled(Box)({
  maxWidth: "100%",
  maxHeight: "100%",
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const ThumbStrip = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  width: "100%",
  maxWidth: "1560px",
  height: "88px",
  margin: "0 auto",
  padding: "0 4px",
  backgroundColor: "rgba(20,20,20,0.9)",
  backdropFilter: "blur(12px)",
  borderRadius: "20px",
  flexShrink: 0,
}));

const ThumbScroll = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "safe center",
  gap: "10px",
  overflowX: "auto",
  flex: 1,
  padding: "0 4px",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
}));

const Thumb = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ selected }) => ({
  position: "relative",
  flexShrink: 0,
  width: "58px",
  height: "58px",
  borderRadius: "12px",
  overflow: "hidden",
  cursor: "pointer",
  backgroundColor: "#111",
  border: selected ? "3px solid #f5f5f5" : "3px solid rgba(255,255,255,0.2)",
  opacity: selected ? 1 : 0.85,
  transition: "border-color 0.15s, opacity 0.15s",
  "&:hover": {
    opacity: 1,
    border: "3px solid rgba(255,255,255,0.45)",
  },
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
}));

const ThumbImg = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});

const ThumbPlaceholder = styled(Box)({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#cfcfcf",
});

const ThumbBadge = styled(Box)({
  position: "absolute",
  top: "4px",
  right: "4px",
  width: "20px",
  height: "20px",
  borderRadius: "50%",
  backgroundColor: "rgba(0,0,0,0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  pointerEvents: "none",
});

const ThumbArrow = styled("button")(() => ({
  flexShrink: 0,
  background: "none",
  border: "none",
  color: "#f5f5f5",
  cursor: "pointer",
  padding: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "8px",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
}));

type Props = {
  open: boolean;
  onClose: () => void;
  files: MediaFile[];
  initialIndex?: number;
  initialFileId?: string | number;
  eftImage?: MediaFile | string;
  eftLabel?: string;
};

const MediaGalleryModal: React.FC<Props> = ({
  open,
  onClose,
  files,
  initialIndex = 0,
  initialFileId,
  eftImage,
  eftLabel = "EFT",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const eftFile = useMemo<MediaFile | null>(() => {
    if (!eftImage) return null;
    if (typeof eftImage === "string") {
      return eftImage ? { id: EFT_FILE_ID, url: eftImage, name: eftLabel, type: EFT_TYPE_MARKER } : null;
    }
    return eftImage.url
      ? {
          ...eftImage,
          id: eftImage.id ?? EFT_FILE_ID,
          name: eftImage.name ?? eftLabel,
          type: EFT_TYPE_MARKER,
        }
      : null;
  }, [eftImage, eftLabel]);

  const items = useMemo(() => {
    const base = files.filter((file): file is MediaFile => !!file?.url);
    const filtered = eftFile
      ? base.filter((file) => file.id !== eftFile.id && file.url !== eftFile.url)
      : base;
    const isVideoKind = (file: MediaFile) => {
      const kind = classify(file);
      return kind === "video" || kind === "youtube" || kind === "vimeo";
    };
    const videos = filtered.filter(isVideoKind);
    const covenants = filtered.filter((file) => !isVideoKind(file) && file.type === "easement_contract");
    const surveys = filtered.filter((file) => !isVideoKind(file) && file.type === "survey_plan");
    const rest = filtered.filter(
      (file) =>
        !isVideoKind(file) && file.type !== "easement_contract" && file.type !== "survey_plan"
    );
    const ordered = [...videos, ...covenants, ...surveys, ...rest];
    return eftFile ? [eftFile, ...ordered] : ordered;
  }, [files, eftFile]);

  const viewableItems = items;

  const [index, setIndex] = useState<number>(0);

  useEffect(() => {
    if (open) {
      const targetIndex =
        initialFileId !== undefined ? viewableItems.findIndex((item) => item.id === initialFileId) : -1;
      const baseIndex = targetIndex >= 0 ? targetIndex : initialIndex;
      const safeIndex = Math.min(Math.max(baseIndex, 0), Math.max(viewableItems.length - 1, 0));
      setIndex(safeIndex);
    }
  }, [open, initialIndex, initialFileId, viewableItems]);

  const goTo = useCallback(
    (target: number) => {
      if (viewableItems.length === 0) return;
      const next = ((target % viewableItems.length) + viewableItems.length) % viewableItems.length;
      setIndex(next);
    },
    [viewableItems]
  );

  const handlePrev = useCallback(() => goTo(index - 1), [goTo, index]);
  const handleNext = useCallback(() => goTo(index + 1), [goTo, index]);

  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key === "ArrowLeft") handlePrev();
      else if (event.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, handlePrev, handleNext, onClose]);

  const current = viewableItems[index];

  const renderStage = () => {
    if (!current) return null;
    const kind = classify(current);

    if (kind === "image") {
      return (
        <Box
          component="img"
          src={current.url}
          alt={current.name || ""}
          // EFT image gets extra horizontal inset so the arrow buttons (left/right at 16px,
          // 44px wide on desktop / 36px on mobile) never overlap it. `contain` preserves
          // the original aspect ratio — never crops. On desktop the EFT image is locked to
          // a fixed 800px height with width adjusting to keep the aspect ratio.
          sx={{
            display: "block",
            maxWidth: isEftFile(current) ? "min(100%, calc(100vw - 160px))" : "100%",
            borderRadius: isEftFile(current) ? "20px" : "0px",
            maxHeight: "100%",
            width: "auto",
            height: !isMobile && isEftFile(current) ? "800px" : "auto",
            objectFit: "contain",
            ...(isMobile && isEftFile(current) ? { maxWidth: "calc(100vw - 96px)" } : {}),
          }}
        />
      );
    }
    if (kind === "youtube") {
      const id = extractYouTubeId(current.url);
      if (!id) return null;
      return (
        <Box
          component="iframe"
          src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0`}
          title={current.name || "YouTube video"}
          sx={{ width: "min(100%, 1280px)", aspectRatio: "16 / 9", border: 0 }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }
    if (kind === "vimeo") {
      const id = extractVimeoId(current.url);
      if (!id) return null;
      return (
        <Box
          component="iframe"
          src={`https://player.vimeo.com/video/${id}?autoplay=1`}
          title={current.name || "Vimeo video"}
          sx={{ width: "min(100%, 1280px)", aspectRatio: "16 / 9", border: 0 }}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      );
    }
    if (kind === "video") {
      return (
        <Box
          component="video"
          src={current.url}
          controls
          autoPlay
          sx={{
            maxWidth: "100%",
            maxHeight: "100%",
            width: "auto",
            height: isMobile ? "auto" : "800px",
            objectFit: "contain",
          }}
        />
      );
    }
    if (kind === "pdf") {
      return (
        <Box
          sx={{
            width: "min(100%, 1180px)",
            height: "100%",
            overflow: "hidden",
            backgroundColor: "#000",
            borderRadius: "20px",
            // boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
          }}
        >
          <Worker workerUrl={PDF_WORKER_URL}>
            <Viewer fileUrl={current.url} defaultScale={isMobile ? SpecialZoomLevel.PageWidth : 2} enableSmoothScroll />
          </Worker>
        </Box>
      );
    }
    return <Box sx={{ color: "#cfcfcf", fontFamily: BODY_FONT, fontSize: "14px" }}>Unsupported media</Box>;
  };

  const renderThumb = (file: MediaFile) => {
    const kind = classify(file);
    if (kind === "image") {
      return <ThumbImg src={file.url} alt={file.name || ""} />;
    }
    if (kind === "youtube") {
      const thumb = youTubeThumb(file.url);
      return thumb ? (
        <>
          <ThumbImg src={thumb} alt={file.name || ""} />
          <ThumbBadge>
            <PlayCircleOutlineIcon sx={{ fontSize: "14px" }} />
          </ThumbBadge>
        </>
      ) : (
        <ThumbPlaceholder>
          <PlayCircleOutlineIcon />
        </ThumbPlaceholder>
      );
    }
    if (kind === "vimeo" || kind === "video") {
      return (
        <ThumbPlaceholder>
          <PlayCircleOutlineIcon />
        </ThumbPlaceholder>
      );
    }
    if (kind === "pdf") {
      if (file.type === "survey_plan") {
        return (
          <ThumbPlaceholder>
            <SurveyIcon />
          </ThumbPlaceholder>
        );
      }
      if (file.type === "easement_contract") {
        return (
          <ThumbPlaceholder>
            <ContractIcon />
          </ThumbPlaceholder>
        );
      }
      return (
        <ThumbPlaceholder>
          <PictureAsPdfIcon />
        </ThumbPlaceholder>
      );
    }
    return <ThumbPlaceholder />;
  };

  return (
    <Modal open={open} onClose={onClose} sx={{ "& .MuiBackdrop-root": { backgroundColor: "transparent" } }}>
      <Backdrop>
        <HeaderBar>
          <Box sx={{ width: "40px" }} />
          <HeaderCenter>
            {current
              ? current.type === "survey_plan"
                ? "Survey"
                : current.type === "easement_contract"
                ? "Ecological Covenant"
                : classify(current) === "video" || classify(current) === "youtube" || classify(current) === "vimeo"
                ? current.name || "Verification video"
                : current.name || ""
              : `${items.length} ${items.length === 1 ? "item" : "items"}`}
          </HeaderCenter>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
            {viewableItems.length > 0 && (
              <Box
                sx={{
                  fontFamily: BODY_FONT,
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#cfcfcf",
                  whiteSpace: "nowrap",
                }}
              >
                {`${index + 1} of ${viewableItems.length}`}
              </Box>
            )}
            <HeaderButton onClick={onClose} aria-label="Close gallery">
              <CloseIcon />
            </HeaderButton>
          </Box>
        </HeaderBar>

        <>
          {viewableItems.length > 1 && (
            <ThumbStrip>
              <ThumbArrow onClick={handlePrev} aria-label="Previous">
                <ArrowBackIosNewIcon fontSize="small" />
              </ThumbArrow>
              <ThumbScroll>
                {viewableItems.map((file, position) => (
                  <Thumb
                    key={`${file.id}-${position}`}
                    selected={position === index}
                    onClick={() => setIndex(position)}
                  >
                    {renderThumb(file)}
                  </Thumb>
                ))}
              </ThumbScroll>
              <ThumbArrow onClick={handleNext} aria-label="Next">
                <ArrowForwardIosIcon fontSize="small" />
              </ThumbArrow>
            </ThumbStrip>
          )}
          <Stage>
            <StageMedia>{renderStage()}</StageMedia>
          </Stage>
        </>
      </Backdrop>
    </Modal>
  );
};

export default MediaGalleryModal;
